import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { INVOICE_DIVERGENCE_STATUSES, INVOICE_INDICATORS, readInvoiceXml } from '@loot/shared';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  api,
  ApiError,
  type InvoiceDetail,
  type InvoiceListItem,
  type InvoiceOrderItems,
  type OrderItemBilling,
  type PayableOrderLine,
  type InvoiceReference,
  type InvoiceStatus,
  type LegalEntity,
  type PayableFormOptions,
} from '../../api.ts';
import { can, useMe } from '../../auth.tsx';
import { changed, FormModal, FormSection } from '../../components/FormModal.tsx';
import { CurrencySelect } from '../../components/forms/CurrencySelect.tsx';
import { ListToolbar, SearchBox } from '../../components/ListFilters.tsx';
import { MasterDeleteButton } from '../../components/MasterDelete.tsx';
import { Modal } from '../../components/Modal.tsx';
import { NetsuiteReferencesSection } from '../../components/NetsuiteReferences.tsx';
import {
  Badge,
  Button,
  EmptyRow,
  ErrorBox,
  Field,
  Input,
  Loading,
  NumCell,
  Select,
  Table,
  Textarea,
} from '../../components/ui.tsx';
import { formatTaxId, partnerChoice } from '../partners/common.tsx';
import { useFormat } from '../purchasing/common.tsx';
import { CardFilterNotice, useCardFilter } from './card-filter.tsx';
import { PayableForm } from './Payables.tsx';

type Tone = 'neutral' | 'ok' | 'off' | 'info' | 'accent';
const TONE: Record<InvoiceStatus, Tone> = {
  received: 'info',
  in_review: 'accent',
  pending: 'neutral',
  rejected: 'off',
  converted: 'ok',
  divergent: 'accent',
  awaiting_order: 'neutral',
};
const STATUSES: InvoiceStatus[] = [
  'received',
  'in_review',
  'pending',
  'rejected',
  'converted',
  'divergent',
  'awaiting_order',
];
const DIVERGENCE: readonly InvoiceStatus[] = INVOICE_DIVERGENCE_STATUSES;
const INDICATOR_LABEL = {
  divergence: 'finance.homeCards.invoiceDivergence.title',
  pending: 'finance.homeCards.invoicePending.title',
} as const;
const KEY = ['finance', 'invoices'];
const today = () => new Date().toISOString().slice(0, 10);

/** Notas de fornecedor (ADR 0081, C9): as do portal e as lançadas pela Loot, até virarem títulos. */
export function InvoiceList() {
  const { t } = useTranslation();
  const fmt = useFormat();
  const { data: me } = useMe();
  const [search, setSearch] = useState('');
  const [status, setStatus] = useState('');
  // Recorte vindo de um cartão da Visão geral: a mesma consulta que contou o número.
  const [indicator, clearIndicator] = useCardFilter(INVOICE_INDICATORS);
  const [adding, setAdding] = useState(false);
  const [openId, setOpenId] = useState<string | null>(null);
  const data = useQuery({
    queryKey: [...KEY, indicator],
    queryFn: () =>
      api.get<{ invoices: InvoiceListItem[] }>(
        indicator ? `/finance/invoices?indicator=${indicator}` : '/finance/invoices',
      ),
  });
  if (data.isPending) return <Loading />;
  if (data.isError) return <ErrorBox error={data.error} />;
  const q = search.trim().toLowerCase();
  const rows = data.data.invoices.filter(
    (r) =>
      (indicator ||
        !status ||
        r.status === status ||
        (status === 'divergence' && DIVERGENCE.includes(r.status))) &&
      (!q ||
        `${r.code} ${r.partnerName ?? ''} ${r.documentNumber} ${r.reference ?? ''} ${r.netsuiteRecordNumber ?? ''}`
          .toLowerCase()
          .includes(q)),
  );
  return (
    <>
      {indicator && (
        <div className="mb-4">
          <CardFilterNotice label={t(INDICATOR_LABEL[indicator])} onClear={clearIndicator} />
        </div>
      )}
      <ListToolbar
        action={
          can(me, 'finance.payable.intake') && (
            <Button onClick={() => setAdding(true)}>{t('finance.invoicesPage.add')}</Button>
          )
        }
      >
        <SearchBox
          value={search}
          onChange={setSearch}
          placeholder={t('finance.invoicesPage.search')}
        />
        <Select
          id="invoices-status"
          aria-label={t('finance.invoicesPage.fields.status')}
          disabled={!!indicator}
          value={status}
          onChange={(e) => setStatus(e.target.value)}
        >
          <option value="">{t('finance.invoicesPage.allStatuses')}</option>
          <option value="divergence">
            {t('finance.invoicesPage.divergenceQueue', {
              count: data.data.invoices.filter((r) => DIVERGENCE.includes(r.status)).length,
            })}
          </option>
          {STATUSES.map((s) => (
            <option key={s} value={s}>
              {t(`finance.invoicesPage.statuses.${s}`)}
            </option>
          ))}
        </Select>
      </ListToolbar>
      <Table
        head={[
          t('finance.invoicesPage.fields.code'),
          t('finance.invoicesPage.fields.legalEntity'),
          t('finance.invoicesPage.fields.partner'),
          t('finance.invoicesPage.fields.document'),
          t('finance.invoicesPage.fields.reference'),
          { label: t('finance.invoicesPage.fields.grossAmount'), align: 'right' as const },
          t('finance.invoicesPage.fields.channel'),
          t('finance.invoicesPage.fields.status'),
          '',
        ]}
      >
        {rows.length === 0 && <EmptyRow colSpan={9}>{t('finance.invoicesPage.empty')}</EmptyRow>}
        {rows.map((r) => (
          <tr key={r.id}>
            <td className="font-mono text-sm">{r.code}</td>
            <td className="font-mono text-sm">{r.legalEntityCode ?? '—'}</td>
            <td>{r.partnerName ?? '—'}</td>
            <td className="text-sm">
              {t(`finance.invoicesPage.kinds.${r.documentKind}`)}{' '}
              <span className="font-mono text-xs">{r.documentNumber}</span>
              <div className="text-xs text-muted">{fmt.date(r.issueDate)}</div>
            </td>
            <td className="font-mono text-xs">
              {r.reference ?? '—'}
              {r.netsuiteRecordNumber && (
                <div className="text-muted">
                  {t('finance.payablesPage.netsuiteShort', { code: r.netsuiteRecordNumber })}
                </div>
              )}
            </td>
            <NumCell>{fmt.money(r.grossAmount, r.currency)}</NumCell>
            <td className="text-sm">{t(`finance.invoicesPage.channels.${r.channel}`)}</td>
            <td>
              <Badge tone={TONE[r.status]}>{t(`finance.invoicesPage.statuses.${r.status}`)}</Badge>
            </td>
            <td className="text-right">
              <div className="flex justify-end gap-2">
                <Button variant="secondary" onClick={() => setOpenId(r.id)}>
                  {t('finance.invoicesPage.open')}
                </Button>
                <MasterDeleteButton table="payables.invoice" id={r.id} compact />
              </div>
            </td>
          </tr>
        ))}
      </Table>
      {adding && <InvoiceForm onDone={() => setAdding(false)} />}
      {openId && <InvoiceModal id={openId} onClose={() => setOpenId(null)} />}
    </>
  );
}

/** Códigos das conferências que recusaram a nota, já traduzidos. */
function rejectionIssues(error: unknown): string[] | null {
  if (!(error instanceof ApiError) || error.code !== 'invoice_rejected') return null;
  return (error.issues as unknown as { code: string }[]).map((i) => i.code);
}

function InvoiceForm({ onDone }: { onDone: () => void }) {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const entities = useQuery({
    queryKey: ['core', 'legal-entities'],
    queryFn: () => api.get<LegalEntity[]>('/core/legal-entities'),
  });
  const [start] = useState(() => ({
    legalEntityId: '',
    partnerId: '',
    documentKind: 'nfe',
    reference: '',
    number: '',
    series: '',
    issueDate: today(),
    currency: 'BRL',
    grossAmount: '',
    accessKey: '',
    netsuiteRecordNumber: '',
    arrivalMeans: 'email',
    arrivalNote: '',
  }));
  const [f, setF] = useState(start);
  const [files, setFiles] = useState<{ xml?: File; pdf?: File; evidence?: File }>({});
  const [xmlRead, setXmlRead] = useState<boolean | null>(null);
  // Quem emitiu a nota, pelo XML: escolhe o fornecedor quando a lista da empresa chega.
  const [issuer, setIssuer] = useState<{ taxId: string; name: string | null } | null>(null);
  const set = (k: keyof typeof f) => (e: { target: { value: string } }) =>
    setF((s) => ({ ...s, [k]: e.target.value }));
  const options = useQuery({
    queryKey: ['finance', 'payables', 'form-options', f.legalEntityId],
    queryFn: () =>
      api.get<PayableFormOptions>(
        `/finance/payables/form-options?legalEntityId=${f.legalEntityId}`,
      ),
    enabled: !!f.legalEntityId,
  });
  const refs = useQuery({
    queryKey: [...KEY, 'references', f.legalEntityId, f.partnerId],
    queryFn: () =>
      api.get<InvoiceReference[]>(
        `/finance/invoices/references?partnerId=${f.partnerId}&legalEntityId=${f.legalEntityId}`,
      ),
    enabled: !!f.legalEntityId && !!f.partnerId,
  });
  const fmt = useFormat();
  const save = useMutation({
    mutationFn: () => {
      const [kind, id] = f.reference.split(':');
      const form = new FormData();
      form.append(
        'data',
        JSON.stringify({
          legalEntityId: f.legalEntityId,
          partnerId: f.partnerId,
          documentKind: f.documentKind,
          reference:
            kind === 'purchase_order'
              ? { kind, purchaseOrderId: id }
              : kind === 'contract'
                ? { kind, contractId: id }
                : { kind: 'none' },
          number: f.number,
          series: f.series,
          issueDate: f.issueDate,
          currency: f.currency,
          grossAmount: f.grossAmount,
          accessKey: f.accessKey || null,
          netsuiteRecordNumber: f.netsuiteRecordNumber || null,
          arrivalMeans: f.arrivalMeans,
          arrivalNote: f.arrivalNote,
        }),
      );
      for (const k of ['xml', 'pdf', 'evidence'] as const) {
        const file = files[k];
        if (file) form.append(k, file, file.name);
      }
      return api.upload<{ id: string }>('/finance/invoices', form);
    },
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: KEY });
      onDone();
    },
  });
  // O XML preenche a nota; o que vem dele é o que vale na conferência.
  const readXml = async (file: File | undefined) => {
    setFiles((s) => ({ ...s, xml: file }));
    if (!file) return setXmlRead(null);
    const data = readInvoiceXml(await file.text());
    setXmlRead(!!data);
    if (!data) return;
    const entity = (entities.data ?? []).find(
      (e) => e.isActive && data.recipientTaxId && e.taxId === data.recipientTaxId,
    );
    setIssuer(data.issuerTaxId ? { taxId: data.issuerTaxId, name: data.issuerName } : null);
    const entityId = f.legalEntityId || entity?.id;
    if (entityId && data.issuerTaxId) {
      // A lista de fornecedores da empresa, para escolher pelo CNPJ de quem emitiu.
      void qc
        .fetchQuery({
          queryKey: ['finance', 'payables', 'form-options', entityId],
          queryFn: () =>
            api.get<PayableFormOptions>(`/finance/payables/form-options?legalEntityId=${entityId}`),
        })
        .then((o) => {
          const match = o.partners.find((p) => p.taxId === data.issuerTaxId);
          if (match) setF((s) => ({ ...s, partnerId: match.id }));
        });
    }
    setF((s) => ({
      ...s,
      legalEntityId: s.legalEntityId || entity?.id || '',
      partnerId: '',
      documentKind: data.kind,
      number: data.number ?? s.number,
      series: data.series ?? s.series,
      issueDate: data.issueDate ?? s.issueDate,
      grossAmount: data.grossAmount ?? s.grossAmount,
      accessKey: data.accessKey ?? s.accessKey,
    }));
  };
  const issuerPartner = issuer
    ? (options.data?.partners ?? []).find((p) => p.taxId === issuer.taxId)
    : undefined;
  const issuerMissing = !!issuer && !!options.data && !issuerPartner && !f.partnerId;
  const issues = rejectionIssues(save.error);
  const text = (k: keyof typeof f, required = true, type = 'text') => (
    <Field label={t(`finance.invoicesPage.fields.${k}`)} required={required}>
      <Input id={`inv-${k}`} type={type} required={required} value={f[k]} onChange={set(k)} />
    </Field>
  );
  const choice = (
    k: keyof typeof f,
    items: { id: string; label: string; search?: string }[],
    required = true,
  ) => (
    <Field label={t(`finance.invoicesPage.fields.${k}`)} required={required}>
      <Select id={`inv-${k}`} required={required} value={f[k]} onChange={set(k)}>
        <option value="">{t('finance.invoicesPage.choose')}</option>
        {items.map((i) => (
          <option key={i.id} value={i.id} data-search={i.search}>
            {i.label}
          </option>
        ))}
      </Select>
    </Field>
  );
  const file = (k: 'xml' | 'pdf' | 'evidence', accept: string, required: boolean) => (
    <Field label={t(`finance.invoicesPage.fields.${k}`)} required={required}>
      <input
        id={`inv-${k}`}
        type="file"
        accept={accept}
        required={required}
        className="text-sm"
        onChange={(e) =>
          k === 'xml'
            ? void readXml(e.target.files?.[0])
            : setFiles((s) => ({ ...s, [k]: e.target.files?.[0] }))
        }
      />
    </Field>
  );
  return (
    <FormModal
      open
      size="xl"
      title={t('finance.invoicesPage.add')}
      description={t('finance.invoicesPage.formHint')}
      dirty={changed(start, f) || Object.keys(files).length > 0}
      saving={save.isPending}
      error={issues ? null : save.error}
      saveLabel={t('finance.invoicesPage.save')}
      onSave={() => save.mutate()}
      onClose={onDone}
    >
      {issues && (
        <div role="alert" className="rounded-md border border-danger/40 bg-danger/5 p-3 text-sm">
          <p className="font-medium">{t('finance.invoicesPage.rejectedTitle')}</p>
          <ul className="mt-1 list-disc pl-5">
            {issues.map((c) => (
              <li key={c}>{t(`finance.invoicesPage.issues.${c}`)}</li>
            ))}
          </ul>
        </div>
      )}
      <FormSection>
        {choice(
          'legalEntityId',
          (entities.data ?? [])
            .filter((e) => e.isActive)
            .map((e) => ({ id: e.id, label: `${e.code} · ${e.legalName}` })),
        )}
        {choice('partnerId', (options.data?.partners ?? []).map(partnerChoice))}
        {choice(
          'reference',
          (refs.data ?? []).map((r) => ({
            id: `${r.kind}:${r.id}`,
            label:
              r.kind === 'purchase_order'
                ? t('finance.invoicesPage.orderOption', {
                    label: r.label,
                    open: fmt.money(r.openAmount, r.currency ?? f.currency),
                  })
                : t('finance.invoicesPage.contractOption', {
                    label: r.title ? `${r.label} · ${r.title}` : r.label,
                  }),
          })),
        )}
        {choice(
          'documentKind',
          (['nfe', 'nfse', 'invoice'] as const).map((k) => ({
            id: k,
            label: t(`finance.invoicesPage.kinds.${k}`),
          })),
        )}
        {file('xml', '.xml,application/xml,text/xml', f.documentKind !== 'invoice')}
        {file('pdf', '.pdf,application/pdf', true)}
        {xmlRead !== null && (
          <p className="text-sm text-muted sm:col-span-2">
            {xmlRead ? t('finance.invoicesPage.xmlRead') : t('finance.invoicesPage.xmlNotRead')}
          </p>
        )}
        {issuerMissing && (
          <p role="alert" className="text-sm text-danger sm:col-span-2">
            {t('finance.invoicesPage.issuerMissing', {
              name: issuer.name ?? '—',
              taxId: formatTaxId('cnpj', issuer.taxId),
            })}
          </p>
        )}
        {text('number')}
        {text('series', false)}
        {text('issueDate', true, 'date')}
        <Field label={t('finance.invoicesPage.fields.currency')} required>
          <CurrencySelect
            id="inv-currency"
            value={f.currency}
            onChange={(currency) => setF((s) => ({ ...s, currency }))}
          />
        </Field>
        {text('grossAmount')}
        {text('accessKey', false)}
        <Field label={t('netsuite.fields.record_number')} hint={t('netsuite.optional')}>
          <Input
            id="inv-netsuite"
            maxLength={100}
            value={f.netsuiteRecordNumber}
            onChange={set('netsuiteRecordNumber')}
          />
        </Field>
        {choice(
          'arrivalMeans',
          (['email', 'paper', 'other'] as const).map((m) => ({
            id: m,
            label: t(`finance.invoicesPage.arrival.${m}`),
          })),
        )}
        {file('evidence', '.pdf,.png,.jpg,.jpeg', false)}
        <Field label={t('finance.invoicesPage.fields.arrivalNote')} className="sm:col-span-2">
          <Textarea id="inv-arrivalNote" value={f.arrivalNote} onChange={set('arrivalNote')} />
        </Field>
      </FormSection>
    </FormModal>
  );
}

function InvoiceModal({ id, onClose }: { id: string; onClose: () => void }) {
  const { t } = useTranslation();
  const fmt = useFormat();
  const qc = useQueryClient();
  const { data: me } = useMe();
  const [reasonFor, setReasonFor] = useState<'pending' | 'reject' | null>(null);
  const [reason, setReason] = useState('');
  const [generating, setGenerating] = useState(false);
  const [pickingItem, setPickingItem] = useState(false);
  const [orderLine, setOrderLine] = useState<PayableOrderLine | null>(null);
  const data = useQuery({
    queryKey: [...KEY, id],
    queryFn: () => api.get<InvoiceDetail>(`/finance/invoices/${id}`),
  });
  const act = useMutation({
    mutationFn: ({ path, body }: { path: string; body?: unknown }) =>
      api.post(`/finance/invoices/${id}/${path}`, body ?? {}),
    onSuccess: async () => {
      setReasonFor(null);
      setReason('');
      await qc.invalidateQueries({ queryKey: KEY });
    },
  });
  const d = data.data;
  const manage = can(me, 'finance.payable.manage');
  const open = d && ['received', 'in_review'].includes(d.status);
  return (
    <Modal open title={d ? `${d.code} · ${d.partnerName ?? ''}` : '…'} onClose={onClose} size="xl">
      {data.isPending && <Loading />}
      {data.isError && <ErrorBox error={data.error} />}
      {d && (
        <div className="flex flex-col gap-4">
          <dl className="grid grid-cols-1 gap-x-6 gap-y-2 text-sm sm:grid-cols-2">
            {(
              [
                ['status', t(`finance.invoicesPage.statuses.${d.status}`)],
                ['channel', t(`finance.invoicesPage.channels.${d.channel}`)],
                [
                  'document',
                  `${t(`finance.invoicesPage.kinds.${d.documentKind}`)} ${d.documentNumber}${d.documentSeries ? `/${d.documentSeries}` : ''}`,
                ],
                ['issueDate', fmt.date(d.issueDate)],
                ['grossAmount', fmt.money(d.grossAmount, d.currency)],
                ['reference', d.reference ?? '—'],
                ['legalEntity', d.legalEntityCode ?? '—'],
                ['accessKey', d.accessKey ?? '—'],
                ...(d.channel === 'backoffice'
                  ? ([
                      [
                        'arrivalMeans',
                        d.arrivalMeans ? t(`finance.invoicesPage.arrival.${d.arrivalMeans}`) : '—',
                      ],
                      ['enteredBy', d.enteredBy ?? '—'],
                    ] as const)
                  : []),
              ] as const
            ).map(([k, v]) => (
              <div key={k}>
                <dt className="text-xs text-muted">{t(`finance.invoicesPage.fields.${k}`)}</dt>
                <dd className={k === 'accessKey' ? 'break-all font-mono text-xs' : ''}>{v}</dd>
              </div>
            ))}
          </dl>
          {d.statusReason && (
            <p className="rounded-md bg-surface-2 p-3 text-sm">
              <span className="font-medium">{t('finance.invoicesPage.reasonForSupplier')}: </span>
              {d.statusReason}
            </p>
          )}
          <section>
            <h3 className="mb-1 text-sm font-semibold">{t('finance.invoicesPage.files')}</h3>
            <ul className="text-sm">
              {d.attachments.map((a) => (
                <li key={a.id}>
                  <a
                    className="text-accent underline"
                    href={`/api/finance/invoices/${d.id}/attachments/${a.id}`}
                  >
                    {t(`finance.invoicesPage.fileKinds.${a.kind}`)} · {a.fileName}
                  </a>
                </li>
              ))}
            </ul>
          </section>
          {d.payables.length > 0 && (
            <section>
              <h3 className="mb-1 text-sm font-semibold">{t('finance.invoicesPage.payables')}</h3>
              <ul className="text-sm">
                {d.payables.map((p) => (
                  <li key={p.id}>
                    <a className="text-accent underline" href={`/finance/payables?open=${p.id}`}>
                      {p.code}
                    </a>{' '}
                    · {fmt.money(p.grossAmount, d.currency)} ·{' '}
                    {t(`finance.payablesPage.statuses.${p.status}`)}
                  </li>
                ))}
              </ul>
            </section>
          )}
          <NetsuiteReferencesSection entityType="finance_invoice" entityId={d.id} />
          <section>
            <h3 className="mb-1 text-sm font-semibold">{t('finance.invoicesPage.history')}</h3>
            <ol className="text-sm">
              {d.events.map((e, i) => (
                <li key={i}>
                  {fmt.dateTime(e.at)} · {t(`finance.invoicesPage.statuses.${e.status}`)}
                  {e.by ? ` · ${e.by}` : ''}
                  {e.reason ? ` · ${e.reason}` : ''}
                </li>
              ))}
            </ol>
          </section>
          {d.divergenceOpen && <DivergencePanel invoice={d} />}
          {manage && !d.divergenceOpen && reasonFor && (
            <Field label={t(`finance.invoicesPage.reasonLabel.${reasonFor}`)} required>
              <Textarea
                id="inv-reason"
                required
                minLength={3}
                value={reason}
                onChange={(e) => setReason(e.target.value)}
              />
            </Field>
          )}
          {act.error && <ErrorBox error={act.error} />}
          {manage && !d.divergenceOpen && d.status !== 'rejected' && d.status !== 'converted' && (
            <div className="flex flex-wrap justify-end gap-2">
              {reasonFor ? (
                <>
                  <Button variant="secondary" onClick={() => setReasonFor(null)}>
                    {t('finance.invoicesPage.back')}
                  </Button>
                  <Button
                    disabled={act.isPending || reason.trim().length < 3}
                    onClick={() => act.mutate({ path: reasonFor, body: { reason } })}
                  >
                    {t(`finance.invoicesPage.actions.${reasonFor}`)}
                  </Button>
                </>
              ) : (
                <>
                  {d.status !== 'in_review' && (
                    <Button variant="secondary" onClick={() => act.mutate({ path: 'review' })}>
                      {t('finance.invoicesPage.actions.review')}
                    </Button>
                  )}
                  {d.status !== 'pending' && (
                    <Button variant="secondary" onClick={() => setReasonFor('pending')}>
                      {t('finance.invoicesPage.actions.pending')}
                    </Button>
                  )}
                  {d.payables.length === 0 && (
                    <Button variant="secondary" onClick={() => setReasonFor('reject')}>
                      {t('finance.invoicesPage.actions.reject')}
                    </Button>
                  )}
                  {open && d.contractId && (
                    <Button onClick={() => setGenerating(true)}>
                      {t('finance.invoicesPage.actions.generate')}
                    </Button>
                  )}
                </>
              )}
            </div>
          )}
          {manage && open && d.orderId && !d.divergenceOpen && (
            <div className="flex flex-wrap items-center justify-end gap-2">
              <p className="text-sm text-muted">{t('finance.invoicesPage.orderPayableHint')}</p>
              <Button onClick={() => setPickingItem(true)}>
                {t('finance.invoicesPage.actions.generate')}
              </Button>
            </div>
          )}
        </div>
      )}
      {pickingItem && d && (
        <OrderItemPicker
          invoice={d}
          onPick={(line) => {
            setPickingItem(false);
            setOrderLine(line);
          }}
          onClose={() => setPickingItem(false)}
        />
      )}
      {orderLine && d && (
        <PayableForm
          invoice={d}
          orderLine={orderLine}
          onDone={() => {
            setOrderLine(null);
            void qc.invalidateQueries({ queryKey: KEY });
          }}
        />
      )}
      {generating && d && (
        <PayableForm
          invoice={d}
          onDone={() => {
            setGenerating(false);
            void qc.invalidateQueries({ queryKey: KEY });
          }}
        />
      )}
    </Modal>
  );
}

type Decision = 'invoice_error' | 'order_error' | 'reject' | 'relink';

/**
 * Divergência de valor (ADR 0081, C9, D1): a nota passou do saldo do pedido. Quem analisa decide se
 * o erro é da nota (pendência ao fornecedor), do pedido (Compras corrige e a nota é religada) ou
 * recusa. Tudo com motivo e no histórico. Quem lançou a nota não analisa.
 */
function DivergencePanel({ invoice: d }: { invoice: InvoiceDetail }) {
  const { t } = useTranslation();
  const fmt = useFormat();
  const qc = useQueryClient();
  const [decision, setDecision] = useState<Decision | null>(null);
  const [reason, setReason] = useState('');
  const [orderId, setOrderId] = useState(d.orderId ?? '');
  const decide = useMutation({
    mutationFn: () =>
      api.post(`/finance/invoices/${d.id}/divergence`, {
        decision,
        reason,
        orderId: decision === 'relink' ? orderId || null : null,
      }),
    onSuccess: async () => {
      setDecision(null);
      setReason('');
      await qc.invalidateQueries({ queryKey: KEY });
    },
  });
  const last = [...d.divergences].reverse().find((x) => x.kind === 'detected');
  const options: Decision[] =
    d.status === 'divergent'
      ? ['invoice_error', 'order_error', 'relink', 'reject']
      : d.status === 'awaiting_order'
        ? ['relink', 'invoice_error', 'reject']
        : ['relink', 'reject'];
  return (
    <section className="rounded-md border border-border p-3">
      <h3 className="mb-1 text-sm font-semibold">{t('finance.invoicesPage.divergence.title')}</h3>
      <p className="mb-2 text-sm text-muted">{t('finance.invoicesPage.divergence.hint')}</p>
      {last && (
        <dl className="mb-2 grid grid-cols-2 gap-x-6 gap-y-1 text-sm sm:grid-cols-4">
          {(
            [
              ['invoiceAmount', fmt.money(last.invoiceAmount, d.currency)],
              ['openAmount', `${last.order ?? ''} · ${fmt.money(last.openAmount, d.currency)}`],
              [
                'difference',
                fmt.money(
                  (Number(last.invoiceAmount ?? 0) - Number(last.openAmount ?? 0)).toFixed(2),
                  d.currency,
                ),
              ],
              ['tolerance', `${Number(last.tolerancePercent ?? 0)}%`],
            ] as const
          ).map(([k, v]) => (
            <div key={k}>
              <dt className="text-xs text-muted">{t(`finance.invoicesPage.divergence.${k}`)}</dt>
              <dd>{v}</dd>
            </div>
          ))}
        </dl>
      )}
      <ol className="mb-2 text-sm">
        {d.divergences.map((x, i) => (
          <li key={i}>
            {fmt.dateTime(x.at)} · {t(`finance.invoicesPage.divergence.kinds.${x.kind}`)}
            {x.order ? ` · ${x.order}` : ''}
            {x.by ? ` · ${x.by}` : ''}
            {x.reason ? ` · ${x.reason}` : ''}
          </li>
        ))}
      </ol>
      {!d.canAnalyzeDivergence ? (
        <p className="text-sm text-muted">{t('finance.invoicesPage.divergence.cannotAnalyze')}</p>
      ) : decision ? (
        <div className="flex flex-col gap-2">
          {decision === 'relink' && (
            <Field label={t('finance.invoicesPage.divergence.order')} required>
              <Select
                id="inv-div-order"
                required
                value={orderId}
                onChange={(e) => setOrderId(e.target.value)}
              >
                <option value="">{t('finance.invoicesPage.choose')}</option>
                {d.divergenceOrders.map((o) => (
                  <option key={o.id} value={o.id}>
                    {t('finance.invoicesPage.orderOption', {
                      label: o.label,
                      open: fmt.money(o.openAmount, d.currency),
                    })}
                  </option>
                ))}
              </Select>
            </Field>
          )}
          <Field label={t(`finance.invoicesPage.divergence.reasonLabel.${decision}`)} required>
            <Textarea
              id="inv-div-reason"
              required
              minLength={3}
              value={reason}
              onChange={(e) => setReason(e.target.value)}
            />
          </Field>
          {decide.error && <ErrorBox error={decide.error} />}
          <div className="flex flex-wrap justify-end gap-2">
            <Button variant="secondary" onClick={() => setDecision(null)}>
              {t('finance.invoicesPage.back')}
            </Button>
            <Button
              disabled={
                decide.isPending || reason.trim().length < 3 || (decision === 'relink' && !orderId)
              }
              onClick={() => decide.mutate()}
            >
              {t(`finance.invoicesPage.divergence.actions.${decision}`)}
            </Button>
          </div>
        </div>
      ) : (
        <div className="flex flex-wrap justify-end gap-2">
          {options.map((o) => (
            <Button key={o} variant="secondary" onClick={() => setDecision(o)}>
              {t(`finance.invoicesPage.divergence.actions.${o}`)}
            </Button>
          ))}
        </div>
      )}
    </section>
  );
}

/**
 * Nota de pedido (ADR 0081, C9, F1): escolher o item do pedido e a parte cobrada (o item inteiro ou
 * um aceite, etapa ou período). O valor sugerido é o menor entre o em aberto do item e o restante
 * da nota; o servidor confere de novo o item, o aceite e os dois saldos.
 */
function OrderItemPicker({
  invoice: d,
  onPick,
  onClose,
}: {
  invoice: InvoiceDetail;
  onPick: (line: PayableOrderLine) => void;
  onClose: () => void;
}) {
  const { t } = useTranslation();
  const fmt = useFormat();
  const data = useQuery({
    queryKey: [...KEY, d.id, 'order-items'],
    queryFn: () => api.get<InvoiceOrderItems>(`/finance/invoices/${d.id}/order-items`),
  });
  const min = (a: string, b: string) => (Number(a) <= Number(b) ? a : b);
  const label = (b: OrderItemBilling) =>
    b.basis === 'acceptance'
      ? t('finance.invoicesPage.orderItems.acceptance')
      : b.basis === 'milestone'
        ? t('finance.invoicesPage.orderItems.milestone', { n: b.milestoneNo })
        : t('finance.invoicesPage.orderItems.period', { date: fmt.date(b.periodStart) });
  const pick = (
    item: InvoiceOrderItems['items'][number],
    billing: OrderItemBilling | null,
    remaining: string,
  ) =>
    onPick({
      orderItemId: item.orderItemId,
      billing,
      amount: min(item.open, remaining),
      description: item.description,
      expenseTypeId: item.expenseTypeId,
      classificationId: item.classificationId,
      costCenterId: item.costCenterId,
    });
  return (
    <Modal open title={t('finance.invoicesPage.orderItems.title')} onClose={onClose} size="lg">
      {data.isPending && <Loading />}
      {data.isError && <ErrorBox error={data.error} />}
      {data.data && (
        <div className="flex flex-col gap-3">
          <p className="text-sm">
            {t('finance.invoicesPage.orderItems.remaining')}:{' '}
            <strong>{fmt.money(data.data.invoiceRemaining, d.currency)}</strong>
          </p>
          <Table
            head={[
              t('finance.invoicesPage.orderItems.item'),
              { label: t('finance.invoicesPage.orderItems.open'), align: 'right' as const },
              '',
            ]}
          >
            {data.data.items.length === 0 && (
              <EmptyRow colSpan={3}>{t('finance.invoicesPage.orderItems.empty')}</EmptyRow>
            )}
            {data.data.items.map((i) => (
              <tr key={i.orderItemId}>
                <td className="text-sm">{i.description}</td>
                <NumCell>{fmt.money(i.open, d.currency)}</NumCell>
                <td className="text-right">
                  <div className="flex flex-wrap justify-end gap-2">
                    {i.fullAvailable && Number(i.open) > 0 && (
                      <Button
                        variant="secondary"
                        onClick={() => pick(i, null, data.data.invoiceRemaining)}
                      >
                        {t('finance.invoicesPage.orderItems.whole')}
                      </Button>
                    )}
                    {i.partialAvailable &&
                      i.parts.map((part, n) => (
                        <Button
                          key={n}
                          variant="secondary"
                          onClick={() => pick(i, part.billing, data.data.invoiceRemaining)}
                        >
                          {label(part.billing)}
                          {part.acceptedAt ? ` · ${fmt.date(part.acceptedAt.slice(0, 10))}` : ''}
                        </Button>
                      ))}
                  </div>
                </td>
              </tr>
            ))}
          </Table>
        </div>
      )}
    </Modal>
  );
}
